import { inject, Injectable, signal } from '@angular/core';
import { EMPTY, catchError, expand, map, of, reduce, switchMap, tap, type Observable } from 'rxjs';

import { OpportunityApiService } from './api/opportunity.api.service';
import { OpportunityWorkspaceApiService } from './api/opportunity-workspace.api.service';
import { type Opportunity, type OpportunityFormData } from './models/opportunity.models';
import { OpportunityStatsService } from './opportunity-stats.service';
import {
  apiStepFromWizardStep,
  type CreateWorkspaceRequestBody,
  mapOpportunityResponseDtoToOpportunity,
  mapPartialOpportunityFormDataToUpdateBody,
  type OpportunityResponseDto,
  type OpportunityUpdateRequestBody,
} from './opportunity.mapper';
import type { OpportunityWizardStep } from './models/opportunity.models';
import type { MomentumUser } from '../../shared/models/momentum-user.models';

export type {
  WorkspaceFilePublicDto,
  WorkspaceFilesIngestionStatusResponseDto,
} from './api/opportunity-workspace.api.service';

export type {
  CreateWorkspaceRequestBody,
  CreateWorkspaceWithOpportunityResponseDto,
  OpportunityResponseDto,
  OpportunityUpdateRequestBody,
} from './api/opportunity.api.service';

const LIST_PAGE_SIZE = 100;

const LIST_FETCH_QUERY = {
  pageSize: LIST_PAGE_SIZE,
  sortBy: 'Created' as const,
  sortAsc: false,
};

/** Root list/active-opportunity cache; HTTP delegated to {@link OpportunityApiService}. */
@Injectable({ providedIn: 'root' })
export class OpportunitiesService {
  private readonly opportunityApi = inject(OpportunityApiService);
  private readonly workspaceApi = inject(OpportunityWorkspaceApiService);
  private readonly statsService = inject(OpportunityStatsService);

  private readonly _items = signal<Opportunity[]>([]);
  private readonly _isLoading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _activeOpportunity = signal<Opportunity | null>(null);

  readonly items = this._items.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly activeOpportunity = this._activeOpportunity.asReadonly();

  loadOpportunities() {
    this._isLoading.set(true);
    this._error.set(null);

    return this.opportunityApi.listOpportunities(LIST_FETCH_QUERY).pipe(
      expand((response) => {
        const token = response.continuationToken;
        if (!token) {
          return EMPTY;
        }
        return this.opportunityApi.listOpportunities({
          ...LIST_FETCH_QUERY,
          continuationToken: token,
        });
      }),
      map((response) => response.items.map((row) => mapOpportunityResponseDtoToOpportunity(row))),
      reduce((accumulated: Opportunity[], page) => [...accumulated, ...page], []),
      tap({
        next: (rows) => {
          this._items.set(rows);
          this._isLoading.set(false);
        },
        error: () => {
          this._error.set('Failed to load opportunities.');
          this._isLoading.set(false);
        },
      }),
      catchError(() => {
        this._error.set('Failed to load opportunities.');
        this._isLoading.set(false);
        return EMPTY;
      }),
    );
  }

  loadOpportunity(id: string, options?: { silent?: boolean }) {
    if (!options?.silent) {
      this._isLoading.set(true);
      this._error.set(null);
    }

    return this.opportunityApi.getOpportunity(id).pipe(
      switchMap((body) => {
        if (!shouldPartialSyncCrmOnLoad(body)) {
          return of(body);
        }
        return this.opportunityApi.syncCrmLink(id, false).pipe(catchError(() => of(body)));
      }),
      map((body) => mapOpportunityResponseDtoToOpportunity(body)),
      tap({
        next: (opportunity) => {
          this._activeOpportunity.set(opportunity);
          if (!options?.silent) {
            this._isLoading.set(false);
          }
        },
        error: () => {
          if (!options?.silent) {
            this._error.set('Failed to load opportunity.');
            this._isLoading.set(false);
          }
        },
      }),
    );
  }

  clearActiveOpportunity(): void {
    this._activeOpportunity.set(null);
  }

  removeOpportunity(opportunityId: string): void {
    this._items.update((items) => items.filter((item) => item.id !== opportunityId));
    if (this._activeOpportunity()?.id === opportunityId) {
      this._activeOpportunity.set(null);
    }
  }

  deleteOpportunity(opportunityId: string, deleteReason: string | null) {
    return this.opportunityApi
      .deleteOpportunity(opportunityId, deleteReason)
      .pipe(tap(() => this.purgeOpportunityFromLocalState(opportunityId)));
  }

  purgeOpportunityFromLocalState(opportunityId: string): void {
    this.removeOpportunity(opportunityId);
    this.statsService.loadStats();
  }

  /** Replaces or appends one opportunity in the in-memory list and syncs active selection. */
  upsertOpportunity(updated: Opportunity): void {
    this._items.update((items) => {
      const exists = items.some((item) => item.id === updated.id);
      return exists
        ? items.map((item) => (item.id === updated.id ? updated : item))
        : [...items, updated];
    });
    if (this._activeOpportunity()?.id === updated.id) {
      this._activeOpportunity.set(updated);
    }
  }

  /** Adds a newly created opportunity from POST /opportunities/workspaces to the in-memory list. */
  registerCreatedOpportunity(dto: OpportunityResponseDto): void {
    this.upsertOpportunity(mapOpportunityResponseDtoToOpportunity(dto));
    this.statsService.loadStats();
  }

  createWorkspace(body: CreateWorkspaceRequestBody = {}) {
    return this.opportunityApi.createWorkspace(body);
  }

  linkOpportunityAccount(opportunityId: string, accountId: string) {
    return this.putOpportunity(opportunityId, { accountId }).pipe(
      map((dto) => mapOpportunityResponseDtoToOpportunity(dto)),
    );
  }

  linkCrmOpportunity(opportunityId: string, crmOpportunityId: string) {
    return this.opportunityApi.linkCrmOpportunity(opportunityId, crmOpportunityId).pipe(
      tap({ next: (dto) => this.applyOpportunityResponseDto(dto) }),
      map((dto) => mapOpportunityResponseDtoToOpportunity(dto)),
    );
  }

  syncCrmLink(opportunityId: string, full = false) {
    return this.opportunityApi.syncCrmLink(opportunityId, full).pipe(
      tap({ next: (dto) => this.applyOpportunityResponseDto(dto) }),
      map((dto) => mapOpportunityResponseDtoToOpportunity(dto)),
    );
  }

  syncCNumber(opportunityId: string) {
    return this.opportunityApi.syncCNumber(opportunityId).pipe(
      tap({ next: (dto) => this.applyOpportunityResponseDto(dto) }),
      map((dto) => mapOpportunityResponseDtoToOpportunity(dto)),
    );
  }

  updateSubmissionDeadline(opportunityId: string, submissionDeadline: string) {
    return this.opportunityApi.updateSubmissionDeadline(opportunityId, submissionDeadline).pipe(
      tap({ next: (dto) => this.applyOpportunityResponseDto(dto) }),
      map((dto) => mapOpportunityResponseDtoToOpportunity(dto)),
    );
  }

  updateOpportunityLinks(opportunityId: string, miroWebUrl: string) {
    return this.opportunityApi
      .updateOpportunityLinks(opportunityId, miroWebUrl.trim())
      .pipe(tap({ next: (dto) => this.applyOpportunityResponseDto(dto, { refreshStats: false }) }));
  }

  startBid(opportunityId: string) {
    return this.opportunityApi.startBid(opportunityId).pipe(
      tap({ next: (dto) => this.applyOpportunityResponseDto(dto) }),
      map((dto) => mapOpportunityResponseDtoToOpportunity(dto)),
    );
  }

  listCrmOpportunities(crmAccountId: string) {
    return this.opportunityApi.listCrmOpportunities(crmAccountId);
  }

  uploadWorkspaceFiles(workspaceId: string, files: File[]) {
    return this.workspaceApi.uploadWorkspaceFiles(workspaceId, files);
  }

  listWorkspaceFiles(workspaceId: string) {
    return this.workspaceApi.listWorkspaceFiles(workspaceId);
  }

  getWorkspaceFile(workspaceId: string, fileId: string) {
    return this.workspaceApi.getWorkspaceFile(workspaceId, fileId);
  }

  getWorkspaceFilesIngestionStatus(workspaceId: string) {
    return this.workspaceApi.getWorkspaceFilesIngestionStatus(workspaceId);
  }

  deleteWorkspaceFile(workspaceId: string, fileId: string) {
    return this.workspaceApi.deleteWorkspaceFile(workspaceId, fileId);
  }

  completeOpportunity(opportunityId: string) {
    return this.opportunityApi.completeOpportunity(opportunityId).pipe(
      tap({
        next: (dto) => {
          const mapped = mapOpportunityResponseDtoToOpportunity(dto);
          this.upsertOpportunity(mapped);
          this.statsService.loadStats();
        },
      }),
    );
  }

  /** PATCH-style PUT body — only sends fields that have values (not opportunity owner). */
  updateOpportunityFromForm(
    opportunityId: string,
    data: Partial<OpportunityFormData>,
    options?: { wizardStep?: OpportunityWizardStep },
  ) {
    const body = mapPartialOpportunityFormDataToUpdateBody(data);
    if (options?.wizardStep !== undefined) {
      body.step = apiStepFromWizardStep(options.wizardStep);
    }

    return this.putOpportunity(opportunityId, body);
  }

  /** Owner changes use POST /api/opportunities/{id}/owner. */
  updateOpportunityOwner(
    opportunityId: string,
    owner: MomentumUser,
  ): Observable<OpportunityResponseDto> {
    return this.opportunityApi
      .reassignOwner(opportunityId, owner)
      .pipe(tap({ next: (dto) => this.applyOpportunityResponseDto(dto) }));
  }

  /** Persists furthest wizard step; BE ignores regressions. Fire-and-forget via {@link advanceWizardStep}. */
  persistWizardStep(opportunityId: string, step: OpportunityWizardStep) {
    return this.putOpportunity(opportunityId, { step: apiStepFromWizardStep(step) });
  }

  /** Updates local navigation immediately; persists step without blocking UI. */
  advanceWizardStep(opportunityId: string, step: OpportunityWizardStep): void {
    this.persistWizardStep(opportunityId, step).subscribe();
  }

  private putOpportunity(opportunityId: string, body: OpportunityUpdateRequestBody) {
    return this.opportunityApi
      .updateOpportunity(opportunityId, body)
      .pipe(tap({ next: (dto) => this.applyOpportunityResponseDto(dto) }));
  }

  private applyOpportunityResponseDto(
    dto: OpportunityResponseDto,
    options?: { refreshStats?: boolean },
  ): void {
    const mapped = mapOpportunityResponseDtoToOpportunity(dto);
    this.upsertOpportunity(mapped);
    if (this._activeOpportunity()?.id === mapped.id) {
      this._activeOpportunity.set(mapped);
    }
    if (options?.refreshStats !== false) {
      this.statsService.loadStats();
    }
  }
}

function shouldPartialSyncCrmOnLoad(dto: OpportunityResponseDto): boolean {
  if (dto.isCompleted) {
    return false;
  }
  const accountId = dto.accountId?.trim();
  const crmOpportunityId = dto.crmOpportunityId?.trim();
  return Boolean(accountId && crmOpportunityId);
}
